import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ServiceWorkerModule } from '@angular/service-worker';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatBadgeModule } from '@angular/material/badge';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Breakpoints } from '@angular/cdk/layout';
import { OverlayModule } from '@angular/cdk/overlay';
import { MatInputModule } from '@angular/material/input';
import { MatDialogModule, MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatRippleModule } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DatetimeAdapter, MAT_DATETIME_FORMATS } from '@mat-datetimepicker/core';
import { MomentDatetimeAdapter, MAT_MOMENT_DATETIME_FORMATS } from '@mat-datetimepicker/moment';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { getFrPaginatorIntl } from '@lib/fr.paginator';
import { NgxMaskModule } from 'ngx-mask';
import { environment } from '../environments/environment';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIntroModule } from '@lib/modules/mat-intro/mat-intro.module';
import { MsalModule, MsalRedirectComponent } from '@azure/msal-angular';
import { BrowserCacheLocation, PublicClientApplication } from '@azure/msal-browser';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';

import 'moment/locale/fr';
import '@lib/prototypes.array.extension';
import '@lib/prototypes.string.extension';
import '@lib/prototypes.number.extension';
import '@lib/prototypes.object.extension';
import '@lib/prototypes.formdata.extension';
import '@lib/prototypes.formgroup.extension';

import { HttpErrorInterceptor } from '@lib/http-error.interceptor';
import { AppRoutingModule } from './app-routing.module';

import { SharedModule } from '@lib/shared.module';
import { MatSpinnerIconModule } from '@lib/modules/mat-spinner-icon/mat-spinner-icon.module';
import { MatNotificationsModule } from '@lib/modules/mat-notifications/mat-notifications.module';
import { MatUserPictureModule } from '@lib/modules/mat-user-picture/mat-user-picture.module';

import { AppComponent } from './app.component';
import { LoginComponent } from './pages/login/login.component';
import { FailComponent } from './pages/fail/fail.component';
import { SupportComponent } from './support/support.component';
import { HomeComponent } from './pages/home/home.component';
import { AppMenuComponent } from './app-menu.component';

@NgModule({
	declarations: [
		AppComponent,
		LoginComponent,
		FailComponent,
		SupportComponent,
        HomeComponent,
        AppMenuComponent
	],
	imports: [
		BrowserModule,
		AppRoutingModule,
		BrowserAnimationsModule,
        ReactiveFormsModule,
        HttpClientModule,
        OverlayModule,
		MatIconModule,
        MatListModule,
        MatCardModule,
        MatInputModule,
        MatBadgeModule,
        MatDialogModule,
        MatAutocompleteModule,
        MatSidenavModule,
        MatToolbarModule,
        MatButtonModule,
        MatMenuModule,
        MatTooltipModule,
        MatRippleModule,
        MatFormFieldModule,
        MatProgressSpinnerModule,
        SharedModule,
        FlexLayoutModule,
        MatSpinnerIconModule,
        MatUserPictureModule,
        MatNotificationsModule,
        MatExpansionModule,
        MatIntroModule,
		NgxMaskModule.forRoot(),
		NgxSkeletonLoaderModule.forRoot({
			animation: 'pulse',
			theme: {
				'max-width.%': 100,
				'margin-bottom.px': 8,
				'border-radius.px': 4,
				'background-color': '#e5e7e8'
			}
		}),
		ServiceWorkerModule.register('ngsw-worker.js', {
			enabled: environment.production,
			registrationStrategy: 'registerImmediately'
		}),
        MsalModule.forRoot(new PublicClientApplication({
            auth: {
                ...environment.msal.auth,
                redirectUri: `https://${location.hostname}/login`,
                navigateToLoginRequestUrl: false
            },
            cache: {
                cacheLocation: BrowserCacheLocation.LocalStorage,
                storeAuthStateInCookie: false
            },
            system: {
                // acquireTokenSilent only looks at the expiration time of the access token to determine when it needs
                // to refresh the tokens (because it wrongly assumes all tokens have the same lifetime).
                // Microsoft changed their backend and the access token now have a random lifespan between 60 and 90 minutes
                // while the ID token has a fixed 1 hour lifetime (at least by default)
                // This means acquireTokenSilent can return expired ID tokens from cache for at most 30 minutes
                // To work around this, we set the renewal offset to 35 minutes before the accessToken expires
                // which will translate to 5 to 35 minutes before the ID token expires
                // Ref: https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/4206
                tokenRenewalOffsetSeconds: 35 * 60
            }
        }), null, null)
	],
	providers: [
		{provide: Window, useValue: window},
		{provide: MatPaginatorIntl, useValue: getFrPaginatorIntl() },
		{provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true},
        {provide: MAT_DATE_LOCALE, useValue: 'fr'},
		{provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS},
		{provide: MAT_DATETIME_FORMATS, useValue: MAT_MOMENT_DATETIME_FORMATS},
		{provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { strict: true }},
		{
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
        },
		{
			provide: DatetimeAdapter,
			useClass: MomentDatetimeAdapter
        },
		{provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: {
			appearance: 'fill'
		}},
		{provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: {
			disableClose: true,
			hasBackdrop: true,
			autoFocus: false,
			restoreFocus:false,
			width: (window.matchMedia(Breakpoints.Handset).matches ? '100vw' : null),
			height: (window.matchMedia(Breakpoints.Handset).matches ? '100vh' : null),
			minWidth: (window.matchMedia(Breakpoints.Handset).matches ? null : '360px'),
			maxWidth: (window.matchMedia(Breakpoints.Handset).matches ? window.outerWidth : '90vw'),
			maxHeight: (window.matchMedia(Breakpoints.Handset).matches ? window.outerHeight : '90vh'),
			panelClass: (window.matchMedia(Breakpoints.Handset).matches ? 'mat-dialog-panel-mobile' : null),
			backdropClass: 'app-acrylic'
		}},
        provideLottieOptions({
            player: () => player
        })
	],
	bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule {
	constructor(private matIconRegistry: MatIconRegistry,
				private domSanitizer: DomSanitizer) {

		/**
		 * Icons
		 */
		this.matIconRegistry.setDefaultFontSetClass('material-symbols-rounded');
		this.matIconRegistry.addSvgIcon('app', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/logo-app.svg'));

		this.matIconRegistry.addSvgIcon('google', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/social-google.svg'));
		this.matIconRegistry.addSvgIcon('onedrive', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/social-onedrive.svg'));
		this.matIconRegistry.addSvgIcon('teams', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/social-teams.svg'));
		this.matIconRegistry.addSvgIcon('outlook', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/social-outlook.svg'));
		this.matIconRegistry.addSvgIcon('powerbi', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/social-powerbi.svg'));
		this.matIconRegistry.addSvgIcon('powerbi-oxance', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/launch-powerbi.svg'));
		this.matIconRegistry.addSvgIcon('facebook', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/social-facebook.svg'));
		this.matIconRegistry.addSvgIcon('twitter', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/social-twitter.svg'));
		this.matIconRegistry.addSvgIcon('linkedin', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/social-linkedin.svg'));
		this.matIconRegistry.addSvgIcon('youtube', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/social-youtube.svg'));
		this.matIconRegistry.addSvgIcon('instagram', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/social-instagram.svg'));
		this.matIconRegistry.addSvgIcon('logmein', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/social-logmein.svg'));
		this.matIconRegistry.addSvgIcon('teamviewer', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/social-teamviewer.svg'));
		this.matIconRegistry.addSvgIcon('v2', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/launch-v2.svg'));
		
        this.matIconRegistry.addSvgIcon('tree-list-vbar', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/mat-tree-list-vbar.svg'));
		this.matIconRegistry.addSvgIcon('tree-list-midbar', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/mat-tree-list-midbar.svg'));
		this.matIconRegistry.addSvgIcon('tree-list-lastbar', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/mat-tree-list-lastbar.svg'));
		this.matIconRegistry.addSvgIcon('tree-list-dot', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/mat-tree-list-dot.svg'));
	}
}
